import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUserSalesGoals, useSalesConsultants } from '@/hooks/useUserSalesGoals';
import { useAdminSalesMetrics } from '@/hooks/useAdminSalesMetrics';
import { useSalesGoals, useConsolidatedSalesGoal } from '@/hooks/useSalesGoals';
import { useGlobalSourceWeights, useSourceActuals } from '@/hooks/useUserSalesGoalsBySource';
import { useReceivedByPeriod } from '@/hooks/useReceivedByPeriod';
import { getSourceLabel } from '@/lib/sourceChannelTypes';

/* ── Achievement badge ── */
function AchBadge({ value }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground text-[11px]">-</span>;
  let cls = 'inline-block px-1.5 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ';
  if (value >= 80) cls += 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
  else if (value >= 40) cls += 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  else cls += 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
  return <span className={cls}>{value.toFixed(1)}%</span>;
}

/* ── Colgroup definitions (mockup-exact percentages) ── */
const SourceColgroup = () => (
  <colgroup>
    <col style={{ width: '22%' }} />
    <col style={{ width: '8%' }} />
    <col style={{ width: '13%' }} />
    <col style={{ width: '9%' }} />
    <col style={{ width: '13%' }} />
    <col style={{ width: '8%' }} />
    <col style={{ width: '13%' }} />
    <col style={{ width: '14%' }} />
  </colgroup>
);

const ConsultantColgroup = () => (
  <colgroup>
    <col style={{ width: '24%' }} />
    <col style={{ width: '14%' }} />
    <col style={{ width: '10%' }} />
    <col style={{ width: '14%' }} />
    <col style={{ width: '9%' }} />
    <col style={{ width: '14%' }} />
    <col style={{ width: '15%' }} />
  </colgroup>
);

export default function SalesMetrics({ dateRange, businessUnit = 'all', stats, isLoading: legacyLoading, t: legacyT }) {
  const { t: hookT } = useTranslation();
  const t = legacyT || hookT;
  const [expandedConsultant, setExpandedConsultant] = useState(null);

  const isLegacyMode = !!(stats || (!dateRange && legacyLoading !== undefined));

  // ── All hooks must be called unconditionally (Rules of Hooks) ──
  const targetDate = dateRange?.from || new Date();
  const year = targetDate.getFullYear();
  const month = targetDate.getMonth() + 1;

  const buValueForGoals = businessUnit !== 'all' ? businessUnit : null;
  const { goals: userGoals, totalGoal: userGoalTotal, isLoading: goalLoading } = useUserSalesGoals(year, month, buValueForGoals);
  const { avgTicket } = useSalesGoals(year, month, buValueForGoals);

  // Team-level goal from sales_goals table (authoritative Meta)
  const buForTeamGoal = businessUnit === 'us' ? 'us' : businessUnit === 'br' ? 'br' : null;
  const { goalAmount: teamGoalSingle, isLoading: teamGoalLoading } = useSalesGoals(year, month, buForTeamGoal);
  const { totalGoal: consolidatedGoal, isLoading: consolidatedLoading } = useConsolidatedSalesGoal(year, month);

  const teamGoal = businessUnit === 'all' ? consolidatedGoal : teamGoalSingle;
  const teamGoalLoading2 = businessUnit === 'all' ? consolidatedLoading : teamGoalLoading;
  const totalGoal = teamGoal > 0 ? teamGoal : userGoalTotal;
  const { data: salesConsultants } = useSalesConsultants();

  const consultantIds = useMemo(() => (salesConsultants || []).map(u => u.id), [salesConsultants]);

  const { data: globalWeights } = useGlobalSourceWeights(year, month, buValueForGoals);

  // Fetch metrics BEFORE useSourceActuals so we can merge consultant IDs
  const { data: metrics, isLoading: metricsLoading, error: metricsError, refetch, isRefetching } = useAdminSalesMetrics(dateRange, businessUnit);
  const { data: receivedData } = useReceivedByPeriod(dateRange, businessUnit);

  // Merge consultant IDs from app_users + RPC results so useSourceActuals fetches ALL
  const allConsultantIds = useMemo(() => {
    const ids = new Set(consultantIds);
    (metrics?.sales_by_salesperson || []).forEach(sp => {
      if (sp.user_id) ids.add(sp.user_id);
    });
    return Array.from(ids);
  }, [consultantIds, metrics?.sales_by_salesperson]);

  const { data: actualsMap } = useSourceActuals(allConsultantIds, year, month, buValueForGoals);

  // ── Legacy mode for Dashboard.jsx (receives stats, isLoading, t) ──
  if (isLegacyMode) {
    const isLoading = legacyLoading;
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }
    const salesStats = stats?.salesMetrics || {};
    const fmtLegacy = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="rounded-lg bg-muted/60 border border-border/40 px-4 py-3">
            <div className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide">{t('dashboard.totalSales', 'Vendas')}</div>
            <div className="text-[22px] font-medium mt-1 tabular-nums leading-tight">{fmtLegacy(salesStats.totalSales)}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{salesStats.dealsCount || 0} deals</div>
          </div>
          <div className="rounded-lg bg-muted/60 border border-border/40 px-4 py-3">
            <div className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide">{t('dashboard.avgTicket', 'Ticket médio')}</div>
            <div className="text-[22px] font-medium mt-1 tabular-nums leading-tight">{fmtLegacy(salesStats.avgTicket)}</div>
          </div>
          <div className="rounded-lg bg-muted/60 border border-border/40 px-4 py-3">
            <div className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide">{t('dashboard.conversionRate', 'Conversão')}</div>
            <div className="text-[22px] font-medium mt-1 tabular-nums leading-tight">{(salesStats.conversionRate || 0).toFixed(1)}%</div>
          </div>
        </div>
      </div>
    );
  }

  // ── Derived values ──
  const totalSalesAmount = metrics?.total_sales_amount || 0;
  const totalDealsCount = metrics?.total_deals_count || 0;
  const salesBySource = metrics?.sales_by_source || [];
  const salesBySalesperson = useMemo(() => {
    if (!metrics?.sales_by_salesperson) return [];
    return metrics.sales_by_salesperson.map(item => ({
      userId: item.user_id,
      userName: item.user_name,
      dealsCount: item.deals_count,
      totalAmount: item.total_amount,
    }));
  }, [metrics?.sales_by_salesperson]);

  const totalReceived = receivedData?.totalReceived || 0;
  const receivedBySource = receivedData?.receivedBySource || new Map();
  const receivedByConsultant = receivedData?.receivedByConsultant || new Map();

  const goalProgress = totalGoal > 0 ? (totalSalesAmount / totalGoal) * 100 : 0;
  const remainingAmount = Math.max(totalGoal - totalSalesAmount, 0);
  const actualAvgTicket = totalDealsCount > 0 ? totalSalesAmount / totalDealsCount : 0;
  const remainingDeals = actualAvgTicket > 0 ? Math.round(remainingAmount / actualAvgTicket) : 0;
  const receivedPct = totalSalesAmount > 0 ? (totalReceived / totalSalesAmount) * 100 : 0;

  const fmt = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  const fmtK = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}K`;
    return `$${amount.toFixed(0)}`;
  };

  // ── Loading / Error ──
  if (metricsLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="text-muted-foreground">{t('common.loading', 'Carregando...')}</p>
      </div>
    );
  }
  if (metricsError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <p className="text-destructive">{t('common.error', 'Erro ao carregar dados')}</p>
        <p className="text-sm text-muted-foreground">{metricsError.message}</p>
      </div>
    );
  }

  // ═══════════════ SOURCE TABLE DATA ═══════════════
  const weights = globalWeights || [];
  const rpcActualsBySource = new Map();
  salesBySource.forEach(item => {
    rpcActualsBySource.set(item.source, { revenue: item.amount, count: item.count });
  });

  const allSources = new Set([
    ...weights.map(w => w.source),
    ...salesBySource.map(s => s.source),
  ]);

  const sourceRows = Array.from(allSources).map(source => {
    const weight = weights.find(w => w.source === source);
    const weightPct = weight ? weight.weight_pct : 0;
    const goalAmount = weight ? (weight.weight_pct / 100) * totalGoal : 0;
    const goalDeals = avgTicket > 0 && goalAmount > 0 ? Math.round(goalAmount / avgTicket) : 0;
    const actual = rpcActualsBySource.get(source) || { revenue: 0, count: 0 };
    const received = receivedBySource.get(source) || 0;
    const ach = goalAmount > 0 ? (actual.revenue / goalAmount) * 100 : null;
    return { source, weightPct, goalAmount, goalDeals, actualRevenue: actual.revenue, actualDeals: actual.count, received, ach };
  }).filter(r => r.goalAmount > 0 || r.actualRevenue > 0)
    .sort((a, b) => b.goalAmount - a.goalAmount || b.actualRevenue - a.actualRevenue);

  const srcTotals = sourceRows.reduce((acc, r) => ({
    weightPct: acc.weightPct + r.weightPct,
    goalAmount: acc.goalAmount + r.goalAmount,
    goalDeals: acc.goalDeals + r.goalDeals,
    actualRevenue: acc.actualRevenue + r.actualRevenue,
    actualDeals: acc.actualDeals + r.actualDeals,
    received: acc.received + r.received,
  }), { weightPct: 0, goalAmount: 0, goalDeals: 0, actualRevenue: 0, actualDeals: 0, received: 0 });

  // ═══════════════ CONSULTANT TABLE DATA ═══════════════
  const consultants = salesConsultants || [];
  const actualsData = actualsMap || new Map();
  const userGoalsMap = new Map();
  (userGoals || []).forEach(g => userGoalsMap.set(g.user_id, Number(g.goal_amount) || 0));

  const consultantRows = consultants.map(user => {
    const userName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown';
    const individualGoal = userGoalsMap.get(user.id) || 0;
    const goalDeals = avgTicket > 0 && individualGoal > 0 ? Math.round(individualGoal / avgTicket) : 0;
    const spData = salesBySalesperson.find(s => s.userId === user.id);
    const actualRevenue = spData?.totalAmount || 0;
    const actualDeals = spData?.dealsCount || 0;
    const received = receivedByConsultant.get(user.id)?.total || 0;
    const ach = individualGoal > 0 ? (actualRevenue / individualGoal) * 100 : null;
    const userActuals = actualsData.get(user.id) || [];
    const receivedBySrc = receivedByConsultant.get(user.id)?.bySource || new Map();

    const breakdown = [];
    const weightSources = new Set();

    (weights || []).forEach(w => {
      weightSources.add(w.source);
      const srcGoal = individualGoal > 0 ? (w.weight_pct / 100) * individualGoal : 0;
      const srcGoalDeals = avgTicket > 0 && srcGoal > 0 ? Math.round(srcGoal / avgTicket) : 0;
      const srcActual = userActuals.find(a => a.source === w.source);
      const srcReceived = receivedBySrc.get(w.source) || 0;
      const srcAch = srcGoal > 0 ? ((srcActual?.revenue || 0) / srcGoal) * 100 : null;
      if (srcGoal > 0 || (srcActual?.revenue || 0) > 0) {
        breakdown.push({
          source: w.source, goalAmount: srcGoal, goalDeals: srcGoalDeals,
          actualRevenue: srcActual?.revenue || 0, actualDeals: srcActual?.deal_count || 0,
          received: srcReceived, ach: srcAch,
        });
      }
    });

    userActuals.forEach(a => {
      if (!weightSources.has(a.source) && a.revenue > 0) {
        breakdown.push({
          source: a.source, goalAmount: 0, goalDeals: 0,
          actualRevenue: a.revenue, actualDeals: a.deal_count,
          received: receivedBySrc.get(a.source) || 0, ach: null,
        });
      }
    });

    return { userId: user.id, userName, individualGoal, goalDeals, actualRevenue, actualDeals, received, ach, breakdown };
  }).filter(r => r.individualGoal > 0 || r.actualRevenue > 0)
    .sort((a, b) => b.actualRevenue - a.actualRevenue);

  // Add rows for salesperson in RPC data but NOT in the consultant list
  const knownConsultantIds = new Set(consultantRows.map(r => r.userId));
  salesBySalesperson.forEach(sp => {
    if (sp.userId && !knownConsultantIds.has(sp.userId) && sp.totalAmount > 0) {
      const received = receivedByConsultant.get(sp.userId)?.total || 0;
      const receivedBySrc = receivedByConsultant.get(sp.userId)?.bySource || new Map();
      const userActuals = actualsData.get(sp.userId) || [];

      // Build breakdown from actuals data
      const breakdown = userActuals
        .filter(a => a.revenue > 0)
        .map(a => ({
          source: a.source, goalAmount: 0, goalDeals: 0,
          actualRevenue: a.revenue, actualDeals: a.deal_count,
          received: receivedBySrc.get(a.source) || 0, ach: null,
        }));

      consultantRows.push({
        userId: sp.userId,
        userName: sp.userName || 'Unknown',
        individualGoal: 0,
        goalDeals: 0,
        actualRevenue: sp.totalAmount,
        actualDeals: sp.dealsCount,
        received,
        ach: null,
        breakdown,
      });
    }
  });
  // Re-sort after adding extra rows
  consultantRows.sort((a, b) => b.actualRevenue - a.actualRevenue);

  // "Não atribuído" — difference between RPC total and sum of known consultants
  const knownConsultantRevenue = consultantRows.reduce((s, r) => s + r.actualRevenue, 0);
  const knownConsultantDeals = consultantRows.reduce((s, r) => s + r.actualDeals, 0);
  const knownConsultantReceived = consultantRows.reduce((s, r) => s + r.received, 0);
  const unattributedRevenue = totalSalesAmount - knownConsultantRevenue;
  const unattributedDeals = totalDealsCount - knownConsultantDeals;
  const unattributedReceived = totalReceived - knownConsultantReceived;

  const conTotals = {
    goalAmount: consultantRows.reduce((s, r) => s + r.individualGoal, 0),
    goalDeals: consultantRows.reduce((s, r) => s + r.goalDeals, 0),
    actualRevenue: knownConsultantRevenue + (unattributedRevenue > 0 ? unattributedRevenue : 0),
    actualDeals: knownConsultantDeals + (unattributedDeals > 0 ? unattributedDeals : 0),
    received: knownConsultantReceived + (unattributedReceived > 0 ? unattributedReceived : 0),
  };
  const conAch = conTotals.goalAmount > 0 ? (conTotals.actualRevenue / conTotals.goalAmount) * 100 : null;

  // ── Shared styles ──
  const thCls = 'text-[12px] font-medium text-muted-foreground py-2.5 px-3';
  const tdCls = 'text-[12px] py-2.5 px-3';
  const tableStyle = { tableLayout: 'fixed', width: '100%', borderCollapse: 'collapse' };

  return (
    <div className="space-y-4">
      {/* ═══════ 5 METRIC CARDS ═══════ */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { label: 'Vendas', value: fmt(totalSalesAmount), sub: `${totalDealsCount} deals`, color: '' },
          { label: 'Recebidos', value: fmt(totalReceived), sub: `${receivedPct.toFixed(0)}% das vendas`, color: 'text-emerald-600 dark:text-emerald-400' },
          { label: 'Meta', value: (goalLoading || teamGoalLoading2) ? '...' : fmt(totalGoal), sub: `${goalProgress.toFixed(1)}% atingido`, color: '' },
          { label: 'Ticket médio', value: fmt(actualAvgTicket), sub: `Meta: ${fmtK(avgTicket)}`, color: '' },
          { label: 'Restante', value: fmt(remainingAmount), sub: `~${remainingDeals} deals`, color: '' },
        ].map((card) => (
          <div key={card.label} className="rounded-lg bg-muted/60 border border-border/40 px-4 py-3">
            <div className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide">{card.label}</div>
            <div className={`text-[22px] font-medium mt-1 tabular-nums leading-tight ${card.color}`}>{card.value}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{card.sub}</div>
          </div>
        ))}
      </div>

      {/* ═══════ SOURCE TABLE ═══════ */}
      <div className="rounded-lg bg-card border border-border overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-border">
            <h3 className="text-[13px] font-semibold text-foreground">Meta vs realizado por source</h3>
            <Button variant="ghost" size="icon" onClick={() => refetch()} disabled={isRefetching} className="h-7 w-7">
              {isRefetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table style={tableStyle}>
              <SourceColgroup />
              <thead>
                <tr className="border-b border-border">
                  <th className={`${thCls} text-left`}>Origem</th>
                  <th className={`${thCls} text-right`}>%</th>
                  <th className={`${thCls} text-right`}>Meta $</th>
                  <th className={`${thCls} text-center`}>M. QTD</th>
                  <th className={`${thCls} text-right`}>Real. $</th>
                  <th className={`${thCls} text-center`}>R. QTD</th>
                  <th className={`${thCls} text-right text-emerald-600 dark:text-emerald-400`}>Receb. $</th>
                  <th className={`${thCls} text-right`}>% ating.</th>
                </tr>
              </thead>
              <tbody>
                {sourceRows.length > 0 ? (
                  <>
                    {sourceRows.map(r => (
                      <tr key={r.source} className="border-b border-border/40 hover:bg-muted/30 transition-colors">
                        <td className={`${tdCls} text-left font-medium`}>{getSourceLabel(r.source, t)}</td>
                        <td className={`${tdCls} text-right tabular-nums`}>{r.weightPct > 0 ? `${r.weightPct}%` : '-'}</td>
                        <td className={`${tdCls} text-right tabular-nums`}>{r.goalAmount > 0 ? fmt(r.goalAmount) : '-'}</td>
                        <td className={`${tdCls} text-center tabular-nums`}>{r.goalDeals > 0 ? r.goalDeals : '-'}</td>
                        <td className={`${tdCls} text-right tabular-nums font-medium`}>{fmt(r.actualRevenue)}</td>
                        <td className={`${tdCls} text-center tabular-nums`}>{r.actualDeals}</td>
                        <td className={`${tdCls} text-right tabular-nums text-emerald-600 dark:text-emerald-400`}>{fmt(r.received)}</td>
                        <td className={`${tdCls} text-right`}><AchBadge value={r.ach} /></td>
                      </tr>
                    ))}
                    {/* Total row */}
                    <tr className="border-t-2 border-border bg-muted/30">
                      <td className={`${tdCls} font-bold`}>Total</td>
                      <td className={`${tdCls} text-right font-bold tabular-nums`}>{srcTotals.weightPct > 0 ? `${Math.round(srcTotals.weightPct)}%` : '-'}</td>
                      <td className={`${tdCls} text-right font-bold tabular-nums`}>{fmt(srcTotals.goalAmount)}</td>
                      <td className={`${tdCls} text-center font-bold tabular-nums`}>{srcTotals.goalDeals}</td>
                      <td className={`${tdCls} text-right font-bold tabular-nums`}>{fmt(srcTotals.actualRevenue)}</td>
                      <td className={`${tdCls} text-center font-bold tabular-nums`}>{srcTotals.actualDeals}</td>
                      <td className={`${tdCls} text-right font-bold tabular-nums text-emerald-600 dark:text-emerald-400`}>{fmt(srcTotals.received)}</td>
                      <td className={`${tdCls} text-right`}><AchBadge value={srcTotals.goalAmount > 0 ? (srcTotals.actualRevenue / srcTotals.goalAmount) * 100 : null} /></td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan={8} className={`${tdCls} text-center text-muted-foreground py-6`}>
                      Sem vendas no período selecionado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      {/* ═══════ CONSULTANT TABLE ═══════ */}
      {(consultantRows.length > 0 || unattributedRevenue > 0) && (
        <div className="rounded-lg bg-card border border-border overflow-hidden">
          <div className="px-5 py-3 border-b border-border">
            <h3 className="text-[13px] font-semibold text-foreground">Meta vs realizado por consultor</h3>
          </div>
          <div className="overflow-x-auto">
            <table style={tableStyle}>
              <ConsultantColgroup />
              <thead>
                <tr className="border-b border-border">
                  <th className={`${thCls} text-left`}>Consultor</th>
                  <th className={`${thCls} text-right`}>Meta $</th>
                  <th className={`${thCls} text-center`}>M. QTD</th>
                  <th className={`${thCls} text-right`}>Real. $</th>
                  <th className={`${thCls} text-center`}>R. QTD</th>
                  <th className={`${thCls} text-right text-emerald-600 dark:text-emerald-400`}>Receb. $</th>
                  <th className={`${thCls} text-right`}>% ating.</th>
                </tr>
              </thead>
              <tbody>
                {consultantRows.map(r => {
                  const isExpanded = expandedConsultant === r.userId;
                  const hasBreakdown = r.breakdown.length > 0;

                  return (
                    <React.Fragment key={r.userId}>
                      {/* Main consultant row */}
                      <tr
                        className={`border-b border-border/40 transition-colors cursor-pointer hover:bg-muted/40 ${isExpanded ? 'bg-muted/20' : ''}`}
                        onClick={() => setExpandedConsultant(isExpanded ? null : r.userId)}
                      >
                        <td className={`${tdCls} font-medium`}>
                          <div className="flex items-center gap-1.5">
                            {isExpanded
                              ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                            {r.userName}
                          </div>
                        </td>
                        <td className={`${tdCls} text-right tabular-nums`}>{fmt(r.individualGoal)}</td>
                        <td className={`${tdCls} text-center tabular-nums`}>{r.goalDeals}</td>
                        <td className={`${tdCls} text-right tabular-nums font-medium`}>{fmt(r.actualRevenue)}</td>
                        <td className={`${tdCls} text-center tabular-nums`}>{r.actualDeals}</td>
                        <td className={`${tdCls} text-right tabular-nums text-emerald-600 dark:text-emerald-400`}>{fmt(r.received)}</td>
                        <td className={`${tdCls} text-right`}><AchBadge value={r.ach} /></td>
                      </tr>

                      {/* Accordion: sub-table with SAME colgroup for alignment */}
                      {isExpanded && (
                        <tr className="border-0">
                          <td colSpan={7} className="p-0">
                            <table style={tableStyle} className="bg-muted/20">
                              <ConsultantColgroup />
                              <tbody>
                                {hasBreakdown ? r.breakdown.map(b => (
                                  <tr key={`${r.userId}-${b.source}`} className="border-b border-border/20">
                                    <td className={`${tdCls} text-muted-foreground pl-9`}>{getSourceLabel(b.source, t)}</td>
                                    <td className={`${tdCls} text-right text-muted-foreground tabular-nums`}>{b.goalAmount > 0 ? fmt(b.goalAmount) : '-'}</td>
                                    <td className={`${tdCls} text-center text-muted-foreground tabular-nums`}>{b.goalDeals > 0 ? b.goalDeals : '-'}</td>
                                    <td className={`${tdCls} text-right font-medium text-muted-foreground tabular-nums`}>{fmt(b.actualRevenue)}</td>
                                    <td className={`${tdCls} text-center text-muted-foreground tabular-nums`}>{b.actualDeals}</td>
                                    <td className={`${tdCls} text-right text-emerald-600/60 dark:text-emerald-400/60 tabular-nums`}>{fmt(b.received)}</td>
                                    <td className={`${tdCls} text-right text-muted-foreground`}>{b.ach !== null ? `${b.ach.toFixed(1)}%` : '-'}</td>
                                  </tr>
                                )) : (
                                  <tr>
                                    <td colSpan={7} className={`${tdCls} pl-9 text-muted-foreground italic`}>
                                      Sem breakdown por source neste período.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* "Não atribuído" row when there are unattributed deals */}
                {unattributedRevenue > 0 && (
                  <tr className="border-b border-border/40 text-muted-foreground">
                    <td className={`${tdCls} italic`}>
                      <div className="flex items-center gap-1.5">
                        <span className="w-3.5" />
                        Não atribuído
                      </div>
                    </td>
                    <td className={`${tdCls} text-right tabular-nums`}>-</td>
                    <td className={`${tdCls} text-center tabular-nums`}>-</td>
                    <td className={`${tdCls} text-right tabular-nums font-medium`}>{fmt(unattributedRevenue)}</td>
                    <td className={`${tdCls} text-center tabular-nums`}>{unattributedDeals > 0 ? unattributedDeals : 0}</td>
                    <td className={`${tdCls} text-right tabular-nums text-emerald-600/60 dark:text-emerald-400/60`}>{fmt(unattributedReceived > 0 ? unattributedReceived : 0)}</td>
                    <td className={`${tdCls} text-right`}>-</td>
                  </tr>
                )}

                {/* Total row */}
                <tr className="border-t-2 border-border bg-muted/30">
                  <td className={`${tdCls} font-bold`}>Total</td>
                  <td className={`${tdCls} text-right font-bold tabular-nums`}>{fmt(conTotals.goalAmount)}</td>
                  <td className={`${tdCls} text-center font-bold tabular-nums`}>{conTotals.goalDeals}</td>
                  <td className={`${tdCls} text-right font-bold tabular-nums`}>{fmt(conTotals.actualRevenue)}</td>
                  <td className={`${tdCls} text-center font-bold tabular-nums`}>{conTotals.actualDeals}</td>
                  <td className={`${tdCls} text-right font-bold tabular-nums text-emerald-600 dark:text-emerald-400`}>{fmt(conTotals.received)}</td>
                  <td className={`${tdCls} text-right`}><AchBadge value={conAch} /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
